import type { input } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { TextField } from '@mui/material';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { MarketGroup, MarketGroupDisplayType, MarketType } from '../../../@types/api';
import { DISPLAY_SELECT_OPTIONS, MARKET_TYPE_USED_FOR_OPTIONS, QUERY_KEYS } from '../../../constants';
import useMutateData from '../../../hooks/useMutateData';

import { DialogType } from '../../../@types';
import { MARKET_GROUP_TYPE, MARKET_TYPE_GROUP_DISPLAY_TYPE } from '../../../constants/enums';
import API_ROUTES from '../../../constants/apiRoutes';
import { useModal } from '../../../contexts/ModalContext';
import { useSports } from '../../../queries';
import { MarketGroupsFormData, marketGroupsSchema } from '../../../schema';
import { ensureArray } from '../../../utils';
import FormFieldStack from '../../atoms/FormFieldStack';
import Switch from '../../atoms/Switch';
import FormModalLayout from '../../layouts/FormModalLayout';
import FormSelect from '../../molecules/FormSelect';
import FormAutocomplete from '../FormAutocomplete';
import { MARKET_GROUP_PAGE_CONFIG } from '../../pages/MarketGroupsPage';
import { getMarketTypeName } from '../../../helpers';

const DEFAULT_FORM_DATA: MarketGroupsFormData = {
  name: '',
  usedFor: '',
  displayType: '',
  marketTypeIds: [],
  mixedGroupIds: [],
  isActive: false,
  sportId: '',
};

type Props = DialogType & {
  displayType: MarketGroupDisplayType;
};

const MarketGroupForm = ({ closeModal, displayType }: Props) => {
  const { item } = useModal<MarketGroup>();

  const currentDisplayType = item?.displayType ?? displayType;
  const isTournamentMarketGroup = currentDisplayType === MARKET_TYPE_GROUP_DISPLAY_TYPE.TOURNAMENT;

  const { createData, updateData } = useMutateData(
    MARKET_GROUP_PAGE_CONFIG[currentDisplayType].endpoint,
    MARKET_GROUP_PAGE_CONFIG[currentDisplayType].queryKey
  );

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
    watch,
    reset,
    setValue,
  } = useForm<input<typeof marketGroupsSchema>, unknown, MarketGroupsFormData>({
    defaultValues: { ...DEFAULT_FORM_DATA, displayType },
    resolver: zodResolver(marketGroupsSchema),
  });

  const sportId = watch('sportId');
  const usedFor = watch('usedFor');
  const marketTypeIds = watch('marketTypeIds');
  const mixedGroupIds = watch('mixedGroupIds');

  const { data: sportsData } = useSports({
    limit: 50,
  });

  useEffect(() => {
    if (item) {
      reset({
        ...item,
        marketTypeIds: item.marketTypes?.map((market) => market.id),
        mixedGroupIds: item.mixedGroups?.map((group) => group.id),
      });
    }
  }, [item, reset]);

  useEffect(() => {
    const subscription = watch((_value, { name, type }) => {
      if (type !== 'change') return;

      if (name === 'sportId' || name === 'usedFor') {
        setValue('marketTypeIds', []);
      }
    });

    return () => subscription.unsubscribe();
  }, [watch, setValue]);

  const handleClose = () => {
    reset(DEFAULT_FORM_DATA);
    closeModal?.();
  };

  const onFormSubmit = (data: MarketGroupsFormData) => {
    if (item) {
      updateData(item.id, data, () => {
        handleClose();
      });
    } else {
      createData(data, () => {
        handleClose();
      });
    }
  };

  return (
    <FormModalLayout
      onSave={handleSubmit(onFormSubmit)}
      label={item ? 'Edit market group' : 'Add new market group'}
      onClose={handleClose}
      isEdit={!!item}
      editEntity={MARKET_GROUP_PAGE_CONFIG[currentDisplayType].endpoint}
    >
      <TextField error={!!errors.name} helperText={errors.name?.message} label='Name' {...register('name')} required />
      <FormSelect
        label='Sport'
        name='sportId'
        required
        control={control}
        error={errors.sportId}
        options={sportsData?.items || []}
        disabled={!!item}
      />
      <FormSelect
        label='Used for'
        name='usedFor'
        required
        control={control}
        error={errors.usedFor}
        options={MARKET_TYPE_USED_FOR_OPTIONS}
        disabled={!!item}
      />
      <FormSelect
        label='Display'
        name='displayType'
        required
        error={errors.displayType}
        control={control}
        options={DISPLAY_SELECT_OPTIONS}
        disabled
        closeOnSelect
      />
      <FormFieldStack label='Status'>
        <Controller name='isActive' control={control} render={({ field }) => <Switch {...field} ref={null} />} />
      </FormFieldStack>
      <FormAutocomplete<MarketType, MarketGroupsFormData>
        name='marketTypeIds'
        control={control}
        label='Markets'
        multiple
        url={isTournamentMarketGroup ? API_ROUTES.MARKET_TYPES_TOURNAMENT_GROUPS_ELIGIBLE : API_ROUTES.MARKET_TYPES}
        queryKey={[QUERY_KEYS.marketTypes, sportId, usedFor, marketTypeIds, isTournamentMarketGroup]}
        queryParams={{
          sportId,
          isActive: true,
          usedFor: ensureArray(usedFor),
          marketTypeIds: ensureArray(marketTypeIds),
          marketTypeGroupType: isTournamentMarketGroup ? MARKET_GROUP_TYPE.NORMAL : undefined,
          type: isTournamentMarketGroup ? undefined : 'match',
        }}
        disabled={!sportId || !usedFor}
        hookEnabled={!!sportId && !!usedFor}
        getOptionLabel={(options, value) => {
          const option = options.find((option) => option?.id === value);
          return option ? getMarketTypeName(option) : '';
        }}
        error={errors.marketTypeIds}
      />
      {isTournamentMarketGroup && (
        <FormAutocomplete<MarketGroup, MarketGroupsFormData>
          name='mixedGroupIds'
          control={control}
          label='Market mix'
          multiple
          url={API_ROUTES.MARKET_TYPE_GROUPS}
          queryKey={[QUERY_KEYS.marketTypeGroups, sportId, usedFor, mixedGroupIds]}
          queryParams={{
            sportId,
            isActive: true,
            usedFor: ensureArray(usedFor),
            mixedGroupIds: ensureArray(mixedGroupIds),
            displayType: currentDisplayType,
            type: MARKET_GROUP_TYPE.MIXED,
          }}
          disabled={!sportId || !usedFor}
          hookEnabled={!!sportId && !!usedFor}
          getOptionLabel={(options, value) => {
            const option = options.find((option) => option?.id === value);
            return option?.name ?? '';
          }}
          error={errors.mixedGroupIds}
        />
      )}
    </FormModalLayout>
  );
};

export default MarketGroupForm;
